import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { ClipboardList, Loader2, Eye, EyeOff, AlertCircle, Shield } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, setAuth, isAuthenticated, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  
  // 2FA states
  const [requires2FA, setRequires2FA] = useState(false);
  const [userId, setUserId] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [verifying2FA, setVerifying2FA] = useState(false);
  const [useBackupCode, setUseBackupCode] = useState(false);

  // Check for success messages in URL
  useEffect(() => {
    if (searchParams.get('verified') === 'true') {
      toast.success('Email verified successfully! You can now log in.');
    }
    if (searchParams.get('reset') === 'true') {
      toast.success('Password reset successfully! Please log in with your new password.');
    }
    // Phase 19.10: prefill email from signup duplicate-email toast action.
    const prefillEmail = searchParams.get('email');
    if (prefillEmail) setEmail(prefillEmail);
  }, [searchParams]);

  React.useEffect(() => {
    if (isAuthenticated && user) {
      // Redirect based on account type
      if (user.account_type === "client_portal") {
        navigate("/client-portal");
      } else if (user.account_type === "employee_portal") {
        navigate("/employee-portal");
      } else if (user.is_platform_admin) {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
    }
  }, [isAuthenticated, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password,
      });

      const data = response.data;

      // Check if 2FA is required
      if (data.requires_2fa) {
        setRequires2FA(true);
        setUserId(data.user_id);
        setLoading(false);
        toast.info("Please enter your 2FA code");
        return;
      }

      // Regular login (no 2FA)
      if (data.access_token) {
        localStorage.setItem("token", data.access_token);
        localStorage.setItem("user", JSON.stringify(data.user));
        setAuth(data.access_token, data.user);
        toast.success("Welcome back!");
        
        // Redirect based on account type
        if (data.user.account_type === "client_portal") {
          navigate("/client-portal");
        } else if (data.user.account_type === "employee_portal") {
          navigate("/employee-portal");
        } else {
          navigate(data.dashboard_redirect || "/dashboard");
        }
      }
    } catch (error) {
      // Phase F.6 iter127 — Network misroute (API host returning HTML) takes priority
      if (error?.code === "NETWORK_MISROUTE") {
        toast.error(error.userMessage || "Cannot reach the API. Check your DNS / network.");
        return;
      }
      const errorDetail = error.response?.data?.detail;

      // Handle email not verified error
      if (errorDetail === 'email_not_verified') {
        setShowVerificationMessage(true);
        toast.error('Please verify your email before logging in');
      } else {
        toast.error(errorDetail || "Invalid credentials");
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    setVerifying2FA(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/verify-2fa`, {
        user_id: userId,
        code: twoFactorCode,
        is_backup_code: useBackupCode,
      });

      const data = response.data;

      if (data.access_token) {
        localStorage.setItem("token", data.access_token);
        localStorage.setItem("user", JSON.stringify(data.user));
        setAuth(data.access_token, data.user);
        toast.success("Welcome back!");
        
        // Redirect based on account type
        if (data.user.account_type === "client_portal") {
          navigate("/client-portal");
        } else if (data.user.account_type === "employee_portal") {
          navigate("/employee-portal");
        } else if (data.user.is_platform_admin) {
          navigate("/admin");
        } else {
          navigate(data.dashboard_redirect || "/dashboard");
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid verification code");
      setTwoFactorCode("");
    } finally {
      setVerifying2FA(false);
    }
  };

  const resetLogin = () => {
    setRequires2FA(false);
    setUserId("");
    setTwoFactorCode("");
    setPassword("");
    setUseBackupCode(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80&w=2070')",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/80 to-slate-900/90" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <div className="flex items-center gap-3 mb-4">
            <img
              src="/business-genie-logo.png"
              alt="Business Genie"
              className="h-14 w-14 rounded-xl object-contain bg-white/5"
              data-testid="login-bg-logo-hero"
            />
            <h1 className="font-heading text-4xl font-bold">Business Genie</h1>
          </div>
          <p className="text-lg text-emerald-100/80 max-w-md">
            Streamline your business processes with intelligent quote
            management, scheduling, employee tracking, and much more…
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <Card className="w-full max-w-md border-0 shadow-none lg:shadow-lg lg:border">
          <CardHeader className="space-y-1 text-center">
            <div className="flex justify-center mb-4 lg:hidden">
              <img
                src="/business-genie-logo.png"
                alt="Business Genie"
                className="w-14 h-14 rounded-xl object-contain bg-white/5"
                data-testid="login-bg-logo-mobile"
              />
            </div>
            <CardTitle className="font-heading text-2xl">
              {requires2FA ? "Two-Factor Authentication" : "Welcome back"}
            </CardTitle>
            <CardDescription>
              {requires2FA 
                ? "Enter the 6-digit code from your authenticator app"
                : "Enter your credentials to access your account"
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Email Verification Warning */}
            {showVerificationMessage && !requires2FA && (
              <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-500 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-amber-800 dark:text-amber-200 font-medium">
                      Email not verified
                    </p>
                    <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                      Please check your inbox for the verification email.{' '}
                      <Link to="/resend-verification" className="underline font-medium">
                        Resend verification email
                      </Link>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* 2FA Form */}
            {requires2FA ? (
              <form onSubmit={handle2FAVerify} className="space-y-4">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                    <Shield className="w-8 h-8 text-primary" />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="2fa-code">
                    {useBackupCode ? "Backup Code" : "Verification Code"}
                  </Label>
                  <Input
                    id="2fa-code"
                    type="text"
                    placeholder={useBackupCode ? "XXXXXXXX" : "000000"}
                    value={twoFactorCode}
                    onChange={(e) => {
                      if (useBackupCode) {
                        // Backup codes are 8 alphanumeric characters
                        setTwoFactorCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8));
                      } else {
                        // TOTP codes are 6 digits
                        setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                      }
                    }}
                    required
                    maxLength={useBackupCode ? 8 : 6}
                    className="text-center text-2xl tracking-widest font-mono"
                    autoFocus
                    data-testid="2fa-code-input"
                  />
                  <p className="text-xs text-muted-foreground text-center">
                    {useBackupCode 
                      ? "Enter one of your 8-character backup codes" 
                      : "Enter the 6-digit code from your authenticator app"}
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={verifying2FA || (useBackupCode ? twoFactorCode.length !== 8 : twoFactorCode.length !== 6)}
                  data-testid="2fa-verify-btn"
                >
                  {verifying2FA ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    "Verify & Sign In"
                  )}
                </Button>

                {/* Toggle between TOTP and backup code */}
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setUseBackupCode(!useBackupCode);
                      setTwoFactorCode("");
                    }}
                    className="text-sm text-primary hover:underline"
                    data-testid="toggle-backup-code-btn"
                  >
                    {useBackupCode 
                      ? "Use authenticator app instead" 
                      : "Lost your phone? Use a backup code"}
                  </button>
                </div>

                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={resetLogin}
                >
                  ← Back to login
                </Button>
              </form>
            ) : (
              /* Regular Login Form */
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <Link
                      to="/forgot-password"
                      className="text-xs text-primary hover:underline"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    "Sign In"
                  )}
                </Button>
              </form>
            )}

            {!requires2FA && (
              <div className="mt-6 text-center text-sm space-y-3">
                <div>
                  <span className="text-muted-foreground">
                    Don't have an account?{" "}
                  </span>
                  <Link
                    to="/signup"
                    className="text-primary hover:underline font-medium"
                    data-testid="register-link"
                  >
                    Start Free Trial
                  </Link>
                </div>
                <div className="text-xs text-muted-foreground">
                  30-day free trial • No credit card required
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
